/* eslint-disable @typescript-eslint/no-explicit-any */

// a function to retry loading a chunk to avoid chunk load error for out of date code
/**
 * Retries the import of a component with lazy loading and refreshes the page if necessary.
 * @param importStatement - A function that returns a promise that resolves to the imported component.
 * @param name - The name of the component being imported.
 * @returns A promise that resolves to the imported component.
 */
export async function lazyRetry<T extends React.ComponentType<any>>(
  importStatement: () => Promise<{ default: T }>,
  name: string,
): Promise<{ default: T }> {
  // Clear any existing retry-{name}-refreshed items
  for (let i = 0; i < window.sessionStorage.length; i++) {
    const key = window.sessionStorage.key(i)
    if (key && key.startsWith('retry-') && key.endsWith('-refreshed')) {
      window.sessionStorage.removeItem(key)
    }
  }

  const ret = await new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false',
    )
    // try to import the component
    // eslint-disable-next-line consistent-return
    importStatement()
      .then((component: any) => {
        window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false') // success so reset the refresh
        resolve(component)
      })
      // eslint-disable-next-line consistent-return
      .catch((error: any) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true') // we are now going to refresh
          return window.location.reload() // refresh the page
        }
        console.error(error)
        reject(error) // Default error behaviour as already tried refresh
      })
  })
  return ret as { default: T }
}
